import { useEffect, useRef, useState } from 'react';
import { useDebouncedValue } from './useDebouncedValue.js';

/**
 * 162: the page and the search box of one admin list, and the rule between them.
 *
 * 🔴 The rule is FR-007: **a changed filter always returns to page 1.** Staying on page 3 of an
 * unfiltered list while narrowing it to two rows shows an empty page, and an empty page reads as
 * "no matches" — so the operator concludes their search found nothing when it found two things.
 * Thirteen screens implementing that separately is thirteen chances to omit it.
 *
 * `resetOn` carries a screen's own filters (a status select, a supplier select) so they obey the
 * same rule as the search box without each screen restating it.
 */
export function useListControls({
  delayMs = 400,
  resetOn = [],
}: { delayMs?: number; resetOn?: unknown[] } = {}) {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput.trim(), delayMs);

  // Compared by VALUE, not by array identity: a caller passing a fresh array literal every render
  // would otherwise re-run this effect on every render and make paging impossible.
  const filters = JSON.stringify([search, ...resetOn]);
  const previous = useRef(filters);

  useEffect(() => {
    // Mutation testing found this guard INERT — the dependency array already prevents the effect
    // from running unless `filters` changed, and on mount `setPage(1)` is a no-op because the page
    // is already 1. It is kept because it states the intent and because the effect must actually
    // READ `filters`: an effect that only lists it as a dependency without using it is what
    // biome's `useExhaustiveDependencies` rejects, and rightly — a watcher that ignores what it
    // watches is one refactor away from watching the wrong thing.
    if (previous.current === filters) return;
    previous.current = filters;
    setPage(1);
  }, [filters]);

  return { page, setPage, searchInput, setSearchInput, search };
}
